import {uniqBy, first} from '@shared/helpers/array'
import {toNumber} from '@shared/helpers/lang'
import {createStateMaker} from '@shared/helpers/object'

import {APIStatus} from '@web/core/constants'

import {FetchAiTEMSResponse} from '../actions/apis/interfaces'
import {GridSize, MaxProductNum, MaxTabNum, MinProductNum} from '../constants'
import {AdStatus, AiTEMSState, AiTEMSTab, DeliveryFee, ProductTypeType} from '../interfaces'

export const getInitialState = (): AiTEMSState => {
    return {
        fetchStatus: APIStatus.IDLE,
        adStatus: AdStatus.IDLE,
        tabs: [],
        page: 0,
        products: [],
    }
}

export const makeMock = createStateMaker<AiTEMSState>(getInitialState)

const getType = ({saleTp, overseaTp}: {saleTp: string; overseaTp: string}): ProductTypeType | undefined => {
    if (saleTp === '2') {
        return 'RENTAL'
    }
    if (saleTp === '8') {
        return 'RESERVATION'
    }
    if (['1', '2'].includes(overseaTp)) {
        return 'OVERSEA'
    }
    return undefined
}

const getRental = ({rentalCont}: {rentalCont?: string}) => {
    if (!rentalCont) {
        return undefined
    }
    const [totalFee, contractPeriod] = rentalCont.split('^')
    return {
        totalFee: toNumber(totalFee) || 0,
        contractPeriod: toNumber(contractPeriod) || 0,
    }
}

const getDelivery = ({
    dvlryFeeCont,
    fastdlvry,
    dlvryFeeCd,
    krwDlvryFee,
    wdTp,
    searchKeyword,
    verticalCd,
}: {
    dvlryFeeCont?: string
    fastdlvry?: string
    dlvryFeeCd: string
    krwDlvryFee: string
    wdTp: string
    searchKeyword?: string
    verticalCd?: string
}): ItemType<AiTEMSTab['products']>['delivery'] => {
    const fastDelivery = ['1', '4', '5'].includes(fastdlvry ?? '')
    const krwFee = toNumber(krwDlvryFee) || 0
    const drFee = toNumber(dvlryFeeCont) || 0

    const isBranch = wdTp === '14' && !['전통시장', '백화점식품관'].includes(searchKeyword ?? '')
    const isKurly = (verticalCd || '').split('|').includes('KURLYNMART')
    const tooltipType = isBranch ? 'BRANCH' : isKurly ? 'KURLY' : undefined

    if (dlvryFeeCd === '1' || dvlryFeeCont === '0') {
        return {
            feeType: DeliveryFee.FREE,
            fee: 0,
            fastDelivery,
            tooltipType,
        }
    }
    if (dlvryFeeCd === '2' || dvlryFeeCont === '-1') {
        return {
            feeType: DeliveryFee.AFTER_PAY,
            fee: krwFee,
            fastDelivery,
        }
    }
    if (dlvryFeeCd === '0' && krwFee > 0) {
        return {
            feeType: DeliveryFee.PRE_PAY,
            fee: krwFee,
            fastDelivery,
            tooltipType,
        }
    }
    if (drFee > 0) {
        return {
            feeType: DeliveryFee.PRE_PAY,
            fee: drFee,
            fastDelivery,
            tooltipType,
        }
    }
    return {
        fastDelivery,
    }
}

const getInstallation = ({installation}: {installation?: string}) => {
    if (installation === 'Y^Y') {
        return true
    }
    return false
}

export const convertTabInfo = (tabs?: FetchAiTEMSResponse['tabs']): AiTEMSState['tabs'] => {
    const uniqTabs = uniqBy((tab) => first(tab.description.textInfo?.highlights) ?? '', tabs)

    const convertedTabs =
        uniqTabs
            ?.map((tab) => {
                const title = {
                    template: tab.description.textInfo.template,
                    highlight: tab.description.textInfo.highlights ?? [],
                }

                const products = tab.items.map((item) => {
                    const {
                        nvMid,
                        mallProductId,
                        catId,
                        mallNo,
                        mallChnlSeq,
                        productTitle,
                        imageUrl,
                        mallProductUrl,
                        mallProdMblUrl,
                        lowPrice,
                        discountRatio,
                        saleTp,
                        overseaTp,
                        rentalCont,
                        dvlryFeeCont,
                        fastdlvry,
                        dlvryFeeCd,
                        krwDlvryFee,
                        installation,
                        wdTp,
                        searchKeyword,
                        verticalCd,
                    } = item
                    const priceNum = toNumber(lowPrice) || 0
                    const discountRatioNum = toNumber(discountRatio) || 0

                    return {
                        name: productTitle,
                        imageUrl,
                        pcUrl: mallProductUrl,
                        moUrl: mallProdMblUrl,
                        price: priceNum,
                        discountRatio: discountRatioNum,
                        type: getType({saleTp, overseaTp}),
                        rental: getRental({rentalCont}),
                        delivery: getDelivery({
                            dvlryFeeCont,
                            fastdlvry,
                            dlvryFeeCd,
                            krwDlvryFee,
                            wdTp,
                            searchKeyword,
                            verticalCd,
                        }),
                        installation: getInstallation({installation}),
                        logData: {
                            saleTp,
                            catId,
                            mallNo,
                            mallChnlSeq,
                            chnlSeq: mallChnlSeq.split(mallNo)?.[1] ?? '',
                            nvMid,
                            name: productTitle,
                            mallProductId,
                            price: priceNum,
                            pcUrl: mallProductUrl,
                            recInfo: tab.recInfo,
                        },
                    }
                })

                return {
                    landingKey: tab.landingInfo,
                    title,
                    products: products.splice(0, Math.floor(products.length / GridSize) * GridSize),
                }
            })
            ?.filter((tab) => {
                const productNum = tab.products.length
                return productNum >= MinProductNum && productNum < MaxProductNum
            })
            .splice(0, MaxTabNum) ?? []

    return convertedTabs
}
